import { memo, useRef, useCallback, useState } from 'react'
import classNames from 'classnames'
import { createUseStyles } from 'react-jss'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import Logo from '@/components/Logo'
import Burger from '@/components/Burger'
import BtnLink from '@/components/BtnLink'
import { useResize } from '@/components/Handlers'
import * as layerActions from '@/actions/layer'
import * as boundsActions from '@/actions/bounds'
import style from './style'

const useStyles = createUseStyles(style)

const Header = () => {
  const { isMenuOpen, strings, layoutStyle, headerNav, isLanding } = useSelector((state) => ({
    isMenuOpen: state.layer.layers.some((layer) => layer.id === 'menu' && layer.isOpen),
    strings: state.options.strings,
    languages: state.locale.languages,
    layoutStyle: state.layout.style,
    isLanding: state.layout.isLanding,
    backButton: state.layout.backButton,
    headerNav: state.nav.header_menu || [],
  }), shallowEqual)

  const classes = useStyles({ layoutStyle })
  const [isBurgerButtonHover, setBurgerButtonHover] = useState(false)
  const $root = useRef()
  const rootBounds = useRef()

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const openMenu = useCallback(() => dispatch(layerActions.openMenu()), [dispatch])
  const closeMenu = useCallback(() => dispatch(layerActions.closeMenu()), [dispatch])
  const setHeaderBounds = useCallback((bounds) => dispatch(boundsActions.setBoundsElement('header', bounds)), [dispatch])

  const handleMouseMove = (e) => {
    if (e.type === 'mouseenter') setBurgerButtonHover(true)
    if (e.type === 'mouseleave') setBurgerButtonHover(false)
  }

  useResize(() => {
    if ($root.current) {
      rootBounds.current = $root.current.getBoundingClientRect()
      setHeaderBounds(rootBounds.current)
    }
  })

  const handleMainMenuClick = useCallback(() => {
    if (isMenuOpen) closeMenu()
    if (!isMenuOpen) openMenu()
  }, [isMenuOpen])

  const preventPressSpacebar = useCallback((node) => {
    if (node !== null) {
      node.addEventListener('keyup', (e) => {
        if (e.keyCode === 32 && e.code === 'Space') e.preventDefault()
      })
    }
  }, [])

  /*------------------------------
  Render Burger
  ------------------------------*/
  const renderBurgerButton = useCallback(() => {
    return (
      <button
        className={classNames({
          [classes.burgerButton]: true,
          [classes.burgerButtonOpen]: isMenuOpen,
        })}
        onClick={handleMainMenuClick}
        aria-label="toggle-main-menu"
        ref={preventPressSpacebar}
        onMouseEnter={(e) => handleMouseMove(e)}
        onMouseLeave={(e) => handleMouseMove(e)}
      >
        <Burger
          className={classes.burgerIcon}
          isActive={isMenuOpen}
          isHover={isBurgerButtonHover}
        />
      </button>
    )
  }, [strings, isMenuOpen, isBurgerButtonHover])

  return (
    <header
      className={classNames({
        [classes.root]: true,
        [classes.noHeader]: isLanding,
      })}
      ref={$root}
    >
      <div className={classes.container}>
        <div className={classes.wrapper}>
          <div className={classes.wrapperBurgerLogo}>
            <div className={classes.burger}>
              {renderBurgerButton()}
            </div>
            <Logo className={classes.logo} />
          </div>
          {headerNav[0] && (
            <BtnLink
              className={classes.btn}
              link={headerNav[0].url}
              label={headerNav[0].title}
            />
          )}
        </div>
      </div>
    </header>
  )
}

export default memo(Header)
